import { api } from "@/lib/api";
import { subscribeBiquote } from "@/lib/biquote";

// Route ticker subscription by provider:
// - binance symbols → wss://data-stream.binance.vision (WebSocket)
// - biquote symbols → SignalR hub wss://biquote.io/hubs/tick, REST poll fallback
export function subscribeTicker(assets, onTick) {
  const arr = Array.isArray(assets) ? assets : [];
  const parsed = arr.map((a) => typeof a === "string" ? { symbol: a, provider: "binance" } : a);
  const binanceSyms = parsed.filter((a) => a.provider === "binance").map((a) => a.symbol);
  const biquoteSyms = parsed.filter((a) => a.provider === "biquote").map((a) => a.symbol);

  let ws = null;
  let biquoteTimer = null;
  let biquoteUnsub = null;
  let backendFallbackTimer = null;
  let yahooTimer = null;
  let wsUsingBackend = false;
  let closed = false;

  // ---- Binance WS (with backend REST fallback) ----
  if (binanceSyms.length) {
    const streams = binanceSyms.map((s) => `${s.toLowerCase()}@ticker`).join("/");
    const startBackendFallback = () => {
      if (wsUsingBackend || closed) return;
      wsUsingBackend = true;
      const tick = async () => {
        if (closed) return;
        try {
          const { data } = await api.get(`/tickers`, { params: { symbols: binanceSyms.join(",") } });
          Object.entries(data || {}).forEach(([sym, v]) => onTick({ symbol: sym, price: v.price, change: v.change }));
        } catch {}
      };
      tick();
      backendFallbackTimer = setInterval(tick, 1000);
    };
    try {
      ws = new WebSocket(`wss://data-stream.binance.vision:9443/stream?streams=${streams}`);
      const failTimeout = setTimeout(() => {
        if (ws && ws.readyState !== 1) {
          try { ws.close(); } catch {}
          startBackendFallback();
        }
      }, 4000);
      ws.onopen = () => clearTimeout(failTimeout);
      ws.onerror = () => {};
      ws.onclose = () => { if (!closed && !wsUsingBackend) startBackendFallback(); };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          const d = msg.data;
          if (!d) return;
          onTick({
            symbol: d.s, price: parseFloat(d.c),
            change: parseFloat(d.P), high: parseFloat(d.h), low: parseFloat(d.l),
          });
        } catch {}
      };
    } catch {
      startBackendFallback();
    }
  }

  // ---- BiQuote SignalR hub (with REST polling fallback) ----
  if (biquoteSyms.length) {
    const startBiquotePolling = () => {
      if (biquoteTimer || closed) return;
      const tick = async () => {
        if (closed) return;
        try {
          const { data } = await api.get(`/tickers`, { params: { symbols: biquoteSyms.join(",") } });
          Object.entries(data || {}).forEach(([sym, v]) => onTick({ symbol: sym, price: v.price, change: v.change }));
        } catch {}
      };
      tick();
      biquoteTimer = setInterval(tick, 1500);
    };
    biquoteUnsub = subscribeBiquote(biquoteSyms, onTick, startBiquotePolling);
  }

  // ---- Yahoo Finance (REST polling via backend) ----
  const yahooSyms = parsed.filter((a) => a.provider === "yahoo").map((a) => a.symbol);
  if (yahooSyms.length) {
    const tick = async () => {
      if (closed) return;
      try {
        const { data } = await api.get(`/tickers`, { params: { symbols: yahooSyms.join(",") } });
        Object.entries(data || {}).forEach(([sym, v]) => onTick({ symbol: sym, price: v.price, change: v.change }));
      } catch {}
    };
    tick();
    yahooTimer = setInterval(tick, 2000);
  }

  return {
    close() {
      closed = true;
      try { ws && ws.close(); } catch {}
      if (biquoteTimer) clearInterval(biquoteTimer);
      if (biquoteUnsub) { try { biquoteUnsub(); } catch {} }
      if (backendFallbackTimer) clearInterval(backendFallbackTimer);
      if (yahooTimer) clearInterval(yahooTimer);
    },
  };
}

// Kline subscription: Binance uses WS. BiQuote SignalR streams ticks; we synthesize
// the currently-open candle from these ticks and periodically pull the closed candles.
export function subscribeKline(asset, interval, onCandle) {
  const symbol = typeof asset === "string" ? asset : asset.symbol;
  const provider = typeof asset === "string" ? "binance" : asset.provider || "binance";
  let ws = null;
  let pollTimer = null;
  let biquoteUnsub = null;
  let closed = false;

  const startPolling = () => {
    if (pollTimer || closed) return;
    const tick = async () => {
      if (closed) return;
      try {
        const data = await fetchKlines(asset, interval, 2);
        if (data && data.length) onCandle(data[data.length - 1]);
      } catch {}
    };
    tick();
    pollTimer = setInterval(tick, provider === "biquote" ? 2000 : 1500);
  };

  if (provider !== "binance") {
    // BiQuote SignalR — stream price ticks & update the last candle in-place.
    // Also poll every 30s to reconcile closed candles / new intervals.
    let currentBar = null;
    const barSeconds = intervalToSeconds(interval);
    const alignBar = (ts, price) => {
      const t = Math.floor(ts / barSeconds) * barSeconds;
      if (!currentBar || currentBar.time !== t) {
        currentBar = { time: t, open: price, high: price, low: price, close: price };
      } else {
        currentBar.high = Math.max(currentBar.high, price);
        currentBar.low = Math.min(currentBar.low, price);
        currentBar.close = price;
      }
      onCandle({ ...currentBar });
    };
    biquoteUnsub = subscribeBiquote([symbol], (tk) => {
      if (tk.symbol !== symbol) return;
      alignBar(Math.floor(Date.now() / 1000), tk.price);
    }, startPolling);
    // Reconcile every 20s to include the closed prior bar
    pollTimer = setInterval(async () => {
      try {
        const data = await fetchKlines(asset, interval, 3);
        if (data && data.length >= 2) onCandle(data[data.length - 2]);
      } catch {}
    }, 20000);
  } else {
    try {
      ws = new WebSocket(`wss://data-stream.binance.vision:9443/ws/${symbol.toLowerCase()}@kline_${interval}`);
      const failTimeout = setTimeout(() => {
        if (ws && ws.readyState !== 1) { try { ws.close(); } catch {}; startPolling(); }
      }, 4000);
      ws.onopen = () => clearTimeout(failTimeout);
      ws.onerror = () => {};
      ws.onclose = () => { if (!closed && !pollTimer) startPolling(); };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          const k = msg.k;
          if (!k) return;
          onCandle({
            time: Math.floor(k.t / 1000),
            open: parseFloat(k.o), high: parseFloat(k.h),
            low: parseFloat(k.l),  close: parseFloat(k.c),
          });
        } catch {}
      };
    } catch { startPolling(); }
  }

  return {
    close() {
      closed = true;
      try { ws && ws.close(); } catch {}
      if (pollTimer) clearInterval(pollTimer);
      if (biquoteUnsub) { try { biquoteUnsub(); } catch {} }
    },
  };
}

function intervalToSeconds(iv) {
  const m = /^(\d+)([smhdw])$/i.exec(iv || "1m");
  if (!m) return 60;
  const n = parseInt(m[1], 10);
  const mult = { s: 1, m: 60, h: 3600, d: 86400, w: 604800 }[m[2].toLowerCase()] || 60;
  return n * mult;
}

export async function fetchKlines(asset, interval = "1m", limit = 300) {
  const symbol = typeof asset === "string" ? asset : asset.symbol;
  try {
    const { data } = await api.get(`/klines/${symbol}`, { params: { interval, limit } });
    return data;
  } catch (e) {
    console.error("klines failed", e);
    return [];
  }
}
